import React from 'react';
import { Card, CardActionArea, Box, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslation } from 'react-i18next';
import { currencyFormatter } from 'components/helpers/currency-formatter';

interface DdtFinancialsCardProps {
  amount: string | number | null | undefined;
  expectedAmount: string | number | null | undefined;
  onRecalculate: () => void;
}

export const DdtFinancialsCard: React.FC<DdtFinancialsCardProps> = ({
  amount,
  expectedAmount,
  onRecalculate
}) => {
  const { t } = useTranslation();
  const prefix = 'pages.ddt.detail';
  const isIncoherent = amount != null && +amount !== +expectedAmount;
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: isIncoherent ? 'warning.main' : 'divider',
        bgcolor: isIncoherent ? 'rgba(237, 108, 2, 0.04)' : 'background.paper',
        cursor: 'pointer',
        my: 2,
        transition:
          'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s',
        '&:hover': {
          transform: 'translateY(-4px) scale(1.01)',
          boxShadow: isIncoherent
            ? '0 12px 24px -10px rgba(237, 108, 2, 0.2)'
            : '0 12px 24px -10px rgba(0, 0, 0, 0.08)',
          borderColor: isIncoherent ? 'warning.dark' : 'primary.light'
        }
      }}>
      <CardActionArea onClick={onRecalculate} sx={{ height: '100%' }}>
        <Box p={3}>
          {/* Header Card */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '0.3px' }}>
              Riepilogo Economico DDT
            </Typography>
            {isIncoherent ? (
              <WarningAmberIcon sx={{ fontSize: 32, color: 'warning.main' }} />
            ) : (
              <CalculateIcon sx={{ fontSize: 32, color: 'primary.main', opacity: 0.8 }} />
            )}
          </Box>

          {/* Importo Effettivo */}
          <Box mb={2}>
            <Typography
              variant="caption"
              display="block"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 0.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.5px'
              }}>
              {t(`${prefix}.amount`)}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {currencyFormatter(amount)}
            </Typography>
          </Box>

          {/* Importo Atteso */}
          <Box>
            <Typography
              variant="caption"
              display="block"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                mb: 0.5,
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: '0.5px'
              }}>
              {t(`${prefix}.invoice-expected`)}
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: isIncoherent ? 'warning.main' : 'primary.main' }}>
              {currencyFormatter(expectedAmount)}
            </Typography>
          </Box>
        </Box>
      </CardActionArea>
    </Card>
  );
};
